import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  BookOpen,
  Search,
  Users,
  Kanban,
  CheckCircle2,
  HelpCircle,
  FolderGit2,
  ShieldCheck,
  Code2,
  TestTube2,
  Crown,
  ChevronDown,
  Layers,
  ArrowRight,
  Sparkles,
  Flame,
  Calendar,
  Settings,
  Filter,
  BarChart3,
  AlertCircle,
  Clock,
  ExternalLink,
} from "lucide-react";
import { GlassCard, CardHeader, T, TYPE, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const SECTIONS = [
  { id: "overview", label: "Overview & Architecture", icon: BookOpen },
  { id: "roles", label: "Role-Wise Workflows", icon: Users },
  { id: "how-to", label: "Step-by-Step Guides (SOP)", icon: Layers },
  { id: "boards-sprints", label: "Boards, Backlog & Sprints", icon: Kanban },
  { id: "faqs", label: "Troubleshooting & FAQs", icon: HelpCircle },
];

const ROLES = [
  {
    title: "Project Admin / Product Manager",
    icon: Crown,
    badge: "Admin / Lead",
    badgeColor: "#7C3AED",
    desc: "Oversees project strategy, workflows, releases, roadmaps and team access.",
    keyDuties: [
      "Create & configure new Projects, Workflows, and Custom Fields",
      "Setup Kanban & Scrum boards, map columns, and set WIP limits",
      "Create high-level Epics and map them on the Roadmap",
      "Define Versions / Releases and track milestone deliveries",
      "Monitor project health, velocity, and cumulative flow metrics",
    ],
    dailyRoutine: [
      "Check Overview dashboard and AI project summary for risks and blockers",
      "Lead Sprint Planning / Backlog refinement with the Tech Lead",
      "Review unassigned or overdue tickets and re-prioritize",
    ],
  },
  {
    title: "Tech Lead / Scrum Master",
    icon: ShieldCheck,
    badge: "Engineering Lead",
    badgeColor: "#2563EB",
    desc: "Manages technical execution, sprint capacity, story estimations and blockers.",
    keyDuties: [
      "Break down Epics into technical user stories and subtasks",
      "Estimate story points or hours with development team",
      "Plan & start active sprints; monitor daily sprint burndown",
      "Manage swimlanes and resolve WIP limit bottlenecks",
      "Complete sprints and roll incomplete tasks into next sprint/backlog",
    ],
    dailyRoutine: [
      "Conduct Daily Standup using the active Kanban / Scrum board",
      "Inspect Aging cards and blocked items",
      "Verify PRs, code reviews, and assign architectural tasks",
    ],
  },
  {
    title: "Software Engineer / Developer",
    icon: Code2,
    badge: "Developer",
    badgeColor: "#059669",
    desc: "Executes technical stories, implements features, and logs work progress.",
    keyDuties: [
      "Review tasks under 'My Work' or board columns",
      "Move card from 'To Do' → 'In Progress' when starting work",
      "Log hours / time spent and remaining estimates",
      "Create technical sub-tasks and link commits/PRs",
      "Transition task to 'In Review' / 'Ready for QA' upon completion",
    ],
    dailyRoutine: [
      "Pick top priority task from the active sprint column",
      "Keep issue status, comments and acceptance criteria updated in real-time",
      "Resolve bugs assigned by QA team immediately",
    ],
  },
  {
    title: "QA & Test Engineer",
    icon: TestTube2,
    badge: "Quality Assurance",
    badgeColor: "#D97706",
    desc: "Validates acceptance criteria, logs defects, and verifies bug fixes.",
    keyDuties: [
      "Monitor 'Ready for QA' or 'Testing' column on the board",
      "Create detailed Bug tickets linked to parent stories/epics",
      "Add reproduction steps, expected vs actual behavior & screenshots",
      "Move verified issues to 'Done' or re-open failed issues with logs",
    ],
    dailyRoutine: [
      "Test incoming cards in the QA queue",
      "Verify resolved bugs and execute regression test suites",
      "Sign-off on Release readiness in the Releases tab",
    ],
  },
];

const GUIDES = [
  {
    title: "1. How to Create and Manage Issues (Tasks, Bugs, Epics)",
    category: "Issues",
    steps: [
      "Click '+ Create' in the top navigation bar from any screen.",
      "Select the Project, Issue Type (Story, Task, Bug, Epic, Sub-task) and enter Summary.",
      "Fill in Description, Assignee, Priority (Highest to Lowest), Story Points, and Due Date.",
      "If working in sprints, select the active Sprint or leave in Backlog.",
      "Click 'Create Issue'. Use the Issues tab to filter, search with JQL, and export to CSV.",
    ],
  },
  {
    title: "2. How to Setup Board Columns, Statuses & WIP Limits",
    category: "Boards",
    steps: [
      "Navigate to Boards → Select your board → Board settings tab.",
      "Click 'Columns & statuses' on the left menu.",
      "Click '+ Add column' to create a new step (e.g., 'Code Review', 'QA Testing').",
      "In 'Workflow statuses in this column', choose which workflow statuses will trigger in this column.",
      "Set WIP Max (e.g. 5 cards) to prevent team overload.",
      "Click 'Save column' to update the live board.",
    ],
  },
  {
    title: "3. How to Run Sprints & Backlog Planning (Scrum)",
    category: "Sprints",
    steps: [
      "Go to the Sprints or Backlog tab in your project.",
      "Click 'Create Sprint', enter Sprint Name (e.g. Sprint 24), Goal, Start and End Dates.",
      "Drag & drop prioritised issues from the Backlog section into the new Sprint container.",
      "Ensure total Story Points match team capacity.",
      "Click 'Start Sprint'. The issues will now appear on the Active Sprint board.",
      "When the sprint duration finishes, click 'Complete Sprint' to close and archive metrics.",
    ],
  },
  {
    title: "4. How to Plan Roadmaps & Milestone Releases",
    category: "Roadmap & Releases",
    steps: [
      "Open the 'Roadmap' tab to visualize quarterly goals and Epics on an interactive Gantt chart.",
      "Drag the start and target dates of Epics to adjust schedules and dependencies.",
      "Go to 'Releases' tab to create a version (e.g. v2.1.0) with expected release date.",
      "Assign issues to the 'Fix Version' field. Track completion progress until 100% ready to deploy.",
    ],
  },
];

const FAQS = [
  {
    q: "Why is the 'To Do' column not showing on my Kanban board?",
    a: "Check if 'Kanban backlog' is enabled in Board settings → 'Estimation & ranking'. When enabled, the first column (To Do) is moved to the dedicated 'Backlog' tab so the board only focuses on active work. If you want it directly on the Kanban board, simply turn OFF the 'Kanban backlog' switch in settings.",
  },
  {
    q: "Why does the 'Workflow statuses in this column' dropdown show 'No options'?",
    a: "Every workflow status in a project can belong to only ONE board column at a time. 'No options' means all available statuses are already assigned to other columns. To reuse a status, edit the column that currently contains it, remove the status, and then it will become available in the dropdown.",
  },
  {
    q: "How do WIP (Work-In-Progress) limits work?",
    a: "WIP limits prevent bottlenecks. If a column has WIP max = 3 and a 4th card is dragged in, the board will display a visual alert or prevent the move depending on your board's 'WIP limits enforcement' policy in Board settings.",
  },
  {
    q: "How are Story Points & Burndown calculated?",
    a: "Story points represent effort. As developers drag cards to columns mapped with 'Done' statuses, the Burndown chart dynamically recalculates remaining points against the ideal guideline.",
  },
];

export default function Docs({ project }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");

  const filteredGuides = GUIDES.filter(
    (g) =>
      g.title.toLowerCase().includes(search.toLowerCase()) ||
      g.steps.some((s) => s.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredFaqs = FAQS.filter(
    (f) =>
      f.q.toLowerCase().includes(search.toLowerCase()) ||
      f.a.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header Banner */}
      <GlassCard sx={{ mb: 3, p: 3, background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)", border: `1px solid ${T.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: T.brand, color: "#fff", display: "flex" }}>
                <BookOpen size={20} />
              </Box>
              <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: T.ink }}>
                Project Management Documentation & SOP
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, maxWidth: 680 }}>
              Complete user guide, role-wise standard operating procedures (SOP), sprint workflows, and board configurations for <strong>{project.name}</strong> ({project.key}).
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search guides, roles, FAQs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color={T.inkMuted} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: "100%", md: 280 }, bgcolor: "#fff", borderRadius: 1 }}
          />
        </Stack>
      </GlassCard>

      {/* Main Content Layout */}
      <Grid container spacing={3}>
        {/* Left Nav Menu */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${T.border}`, borderRadius: 2, bgcolor: "#fff" }}>
            <Typography sx={{ ...labelSx, px: 1.5, py: 1, fontSize: "0.72rem", color: T.inkMuted }}>
              DOCUMENTATION SECTIONS
            </Typography>
            <List dense disablePadding>
              {SECTIONS.map((sec) => {
                const Icon = sec.icon;
                const active = activeTab === sec.id;
                return (
                  <ListItemButton
                    key={sec.id}
                    selected={active}
                    onClick={() => setActiveTab(sec.id)}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.5,
                      py: 1,
                      bgcolor: active ? `${T.brand}14 !important` : "transparent",
                      color: active ? T.brand : T.ink,
                      fontWeight: active ? 700 : 500,
                      "&:hover": { bgcolor: "#F1F5F9" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: active ? T.brand : T.inkMuted }}>
                      <Icon size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={sec.label}
                      primaryTypographyProps={{ fontSize: TYPE.small, fontWeight: active ? 700 : 500 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          {/* Quick Stats Card */}
          <Paper elevation={0} sx={{ p: 2, mt: 2, border: `1px solid ${T.border}`, borderRadius: 2, bgcolor: "#FAFAFA" }}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, mb: 1, color: T.ink }}>
              Project Info
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Project Key:</Typography>
                <Chip size="small" label={project.key} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Sprints Enabled:</Typography>
                <Chip size="small" label={project.sprintsEnabled ? "Yes (Scrum)" : "Kanban"} color={project.sprintsEnabled ? "primary" : "default"} sx={{ height: 20, fontSize: "0.7rem" }} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Your Role:</Typography>
                <Typography sx={{ fontSize: TYPE.micro, fontWeight: 700 }}>{project.myRole || "Member"}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Right Content Area */}
        <Grid item xs={12} md={9}>
          {/* SECTION: Overview */}
          {(activeTab === "overview" || search) && (
            <GlassCard sx={{ mb: 3, p: 3 }}>
              <CardHeader icon={BookOpen} title="1. System Architecture & Lifecycle" subtitle="Core components and how tasks flow through the CRM Project Management suite." />
              <Typography sx={{ fontSize: TYPE.body, color: T.inkSecondary, mb: 2 }}>
                JustTawk Project Management is a unified workspace designed for Agile teams. It brings together <strong>Issue Tracking</strong>, <strong>Kanban & Scrum Boards</strong>, <strong>Roadmaps</strong>, and <strong>Automated Analytics</strong>.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { title: "1. Backlog & Planning", desc: "Ideas, feature requests and technical tasks are captured as Epics and Stories in the Backlog.", color: "#3B82F6" },
                  { title: "2. Sprint Execution", desc: "Active work is committed into Sprints or pulled into Kanban WIP columns for development.", color: "#8B5CF6" },
                  { title: "3. QA & Code Review", desc: "Completed tasks move through verification columns with automated validation rules.", color: "#F59E0B" },
                  { title: "4. Release & Delivery", desc: "Issues linked to Fix Versions are bundled, deployed, and tracked with release notes.", color: "#10B981" },
                ].map((step, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${T.border}`, bgcolor: "#fff", height: "100%" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, color: step.color, mb: 0.5 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>
                        {step.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </GlassCard>
          )}

          {/* SECTION: Roles */}
          {(activeTab === "roles" || search) && (
            <GlassCard sx={{ mb: 3, p: 3 }}>
              <CardHeader icon={Users} title="2. Role-Based Workflows & Responsibilities" subtitle="Standard operating procedures defined by team function." />
              <Grid container spacing={2}>
                {ROLES.map((role, idx) => {
                  const Icon = role.icon;
                  return (
                    <Grid item xs={12} key={idx}>
                      <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${T.border}`, borderRadius: 2, bgcolor: "#fff" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: `${role.badgeColor}15`, color: role.badgeColor, display: "flex" }}>
                              <Icon size={20} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: T.ink }}>
                                {role.title}
                              </Typography>
                              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>
                                {role.desc}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip size="small" label={role.badge} sx={{ bgcolor: `${role.badgeColor}15`, color: role.badgeColor, fontWeight: 700 }} />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={{ ...labelSx, fontSize: "0.68rem", color: T.inkMuted, mb: 0.5 }}>
                              KEY RESPONSIBILITIES
                            </Typography>
                            <Stack spacing={0.5}>
                              {role.keyDuties.map((d, i) => (
                                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                                  <CheckCircle2 size={13} color="#10B981" style={{ marginTop: 3, flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{d}</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={{ ...labelSx, fontSize: "0.68rem", color: T.inkMuted, mb: 0.5 }}>
                              DAILY CADENCE & ROUTINE
                            </Typography>
                            <Stack spacing={0.5}>
                              {role.dailyRoutine.map((r, i) => (
                                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                                  <Clock size={13} color="#6366F1" style={{ marginTop: 3, flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{r}</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </GlassCard>
          )}

          {/* SECTION: How-To Guides */}
          {(activeTab === "how-to" || activeTab === "boards-sprints" || search) && (
            <GlassCard sx={{ mb: 3, p: 3 }}>
              <CardHeader icon={Layers} title="3. Step-by-Step Execution Guides (SOP)" subtitle="Clear step-by-step procedures for common project tasks." />
              <Stack spacing={2}>
                {filteredGuides.map((guide, idx) => (
                  <Accordion key={idx} defaultExpanded={idx === 0} sx={{ border: `1px solid ${T.border}`, borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: "none" }}>
                    <AccordionSummary expandIcon={<ChevronDown size={18} />}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip size="small" label={guide.category} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#F1F5F9" }} />
                        <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, color: T.ink }}>
                          {guide.title}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      <Stack spacing={1}>
                        {guide.steps.map((step, sIdx) => (
                          <Stack key={sIdx} direction="row" spacing={1.5} alignItems="flex-start">
                            <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: T.brand, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, flexShrink: 0 }}>
                              {sIdx + 1}
                            </Box>
                            <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, pt: 0.2 }}>
                              {step}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </GlassCard>
          )}

          {/* SECTION: FAQs & Troubleshooting */}
          {(activeTab === "faqs" || search) && (
            <GlassCard sx={{ p: 3 }}>
              <CardHeader icon={HelpCircle} title="4. Frequently Asked Questions & Troubleshooting" subtitle="Instant solutions to common board, column, and sprint configuration issues." />
              <Stack spacing={1.5}>
                {filteredFaqs.map((faq, idx) => (
                  <Paper key={idx} elevation={0} sx={{ p: 2, border: `1px solid ${T.border}`, borderRadius: 2, bgcolor: "#fff" }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
                      <AlertCircle size={16} color="#E11D48" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, color: T.ink }}>
                        {faq.q}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, pl: 3 }}>
                      {faq.a}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </GlassCard>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
